/**
 * Who an ignored email report type came from. The list used to show only the
 * template key (email:prova-io:daily); the company is what a person recognises
 * (owner ask 2026-09-16). Best source first: the sender's display name
 * ("Swaarm Reports <noreply@swaarm.com>"), then the sender's domain, then the
 * domain slug inside the key - each with the mail-ish and TLD parts dropped
 * and the rest title-cased ("mail.big-corp.co.uk" → "Big Corp").
 */
const TLD_LIKE = new Set(['com', 'net', 'org', 'io', 'co', 'uk', 'de', 'eu', 'us', 'ai', 'app', 'info', 'biz', 'me', 'tv', 'fr', 'es', 'it', 'nl', 'se', 'no', 'dk', 'ch', 'at', 'pl', 'ru', 'in', 'br', 'ca', 'au', 'nz', 'jp', 'cn', 'hk', 'sg', 'ie', 'be', 'pt', 'cz', 'ro', 'ua', 'il', 'tr', 'mx', 'ar']);
const MAIL_LIKE = new Set(['mail', 'email', 'mailer', 'mailgun', 'smtp', 'send', 'reports', 'report', 'stats', 'noreply', 'no-reply', 'notifications', 'notify', 'news', 'info', 'em', 'e', 'm', 'www']);

function titleCase(slug: string): string {
  return slug
    .split(/[-_.]+/)
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
}

/** A company-ish name from a domain: the last label that is not a TLD-like or mail-ish word. */
export function companyFromDomain(domain: string): string | null {
  const parts = domain.toLowerCase().split('.').filter(Boolean);
  while (parts.length > 1 && TLD_LIKE.has(parts[parts.length - 1]!)) parts.pop();
  while (parts.length > 1 && MAIL_LIKE.has(parts[0]!)) parts.shift();
  const pick = parts[parts.length - 1];
  return pick ? titleCase(pick) : null;
}

/** The display name in a sender header, if there is one: `Name <addr>` or `"Name" <addr>`. */
export function senderDisplayName(sender: string): string | null {
  const m = /^\s*"?([^"<]+?)"?\s*<[^>]+>\s*$/.exec(sender);
  const name = m?.[1]?.trim();
  return name && !name.includes('@') ? name : null;
}

/** The address part of a sender header (`addr` or `Name <addr>`), lower-cased. */
export function senderAddress(sender: string): string {
  const m = /<([^>]+)>/.exec(sender);
  return (m?.[1] ?? sender).trim().toLowerCase();
}

/**
 * The name to lead an ignored type's row with. `host` is the template key
 * (email:<domain-slug>:<stem>) — the fallback when no message was logged for
 * it; a slug lost its dots, so its TLD-like tail words are dropped instead.
 */
export function companyName(sender: string | null, host: string): string {
  if (sender) {
    const display = senderDisplayName(sender);
    if (display) return display;
    const at = senderAddress(sender).split('@');
    if (at.length === 2 && at[1]) {
      const fromDomain = companyFromDomain(at[1]);
      if (fromDomain) return fromDomain;
    }
  }
  const slug = host.split(':')[1] ?? '';
  const words = slug.split('-').filter(Boolean);
  while (words.length > 1 && TLD_LIKE.has(words[words.length - 1]!)) words.pop();
  while (words.length > 1 && MAIL_LIKE.has(words[0]!)) words.shift();
  return words.length ? titleCase(words.join('-')) : host;
}
